import React, { useState, useEffect, useCallback } from 'react';
import { conceptosPagoAPI, pagosAPI } from '../../services/api';

const TIPOS = [
  { value: 'mensualidad', label: 'Mensualidad', icon: '📅' },
  { value: 'torneo', label: 'Torneo', icon: '🏆' },
  { value: 'inscripcion', label: 'Inscripcion', icon: '📝' },
  { value: 'uniforme', label: 'Uniforme', icon: '👕' },
  { value: 'otro', label: 'Otro', icon: '📋' },
];

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const initialForm = {
  nombre: '',
  tipo: 'mensualidad',
  descripcion: '',
  // Divisas price (always USD)
  precio_usd: '',
  precio_media_beca_usd: '',
  // Bolivares price (amount in the chosen currency, multiplied by its BCV rate)
  precio_bs_valor: '',
  precio_bs_media_beca_valor: '',
  tasa_bs: 'USD', // 'USD' = use BCV/Dolar rate, 'EUR' = use BCV/Euro rate
  mes_correspondiente: '',
  ano_correspondiente: new Date().getFullYear(),
  fecha_limite: '',
};

export default function ConceptosPago() {
  const [conceptos, setConceptos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [tasaUSD, setTasaUSD] = useState(null);
  const [tasaEUR, setTasaEUR] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const cargarConceptos = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filtroTipo !== 'todos') params.tipo = filtroTipo;
      const res = await conceptosPagoAPI.getAll(params);
      setConceptos(res.data);
    } catch (err) {
      console.error('Error cargando conceptos:', err);
    } finally {
      setLoading(false);
    }
  }, [filtroTipo]);

  useEffect(() => {
    cargarConceptos();
    pagosAPI.getTasaBCV().then(res => {
      setTasaUSD(res.data.tasa);
      setTasaEUR(res.data.tasaEUR);
    }).catch(() => {});
  }, [cargarConceptos]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const data = {
        nombre: form.nombre,
        tipo: form.tipo,
        descripcion: form.descripcion || null,
        // Divisas (always USD)
        precio_usd: parseFloat(form.precio_usd) || 0,
        precio_media_beca_usd: form.precio_media_beca_usd ? parseFloat(form.precio_media_beca_usd) : null,
        // Bs pricing
        precio_bs_valor: form.precio_bs_valor ? parseFloat(form.precio_bs_valor) : null,
        precio_bs_media_beca_valor: form.precio_bs_media_beca_valor ? parseFloat(form.precio_bs_media_beca_valor) : null,
        tasa_bs: form.tasa_bs || 'USD',
        // Legacy fields (keep for backward compat)
        precio_eur: null,
        precio_media_beca_eur: null,
        // Period
        ano_correspondiente: form.ano_correspondiente ? parseInt(form.ano_correspondiente) : null,
        mes_correspondiente: form.mes_correspondiente || null,
        fecha_limite: form.fecha_limite || null,
      };

      if (editingId) {
        await conceptosPagoAPI.update(editingId, data);
      } else {
        await conceptosPagoAPI.create(data);
      }

      setShowForm(false);
      setEditingId(null);
      setForm(initialForm);
      cargarConceptos();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (concepto) => {
    setForm({
      nombre: concepto.nombre || '',
      tipo: concepto.tipo || 'mensualidad',
      descripcion: concepto.descripcion || '',
      precio_usd: concepto.precio_usd || '',
      precio_media_beca_usd: concepto.precio_media_beca_usd || '',
      precio_bs_valor: concepto.precio_bs_valor || '',
      precio_bs_media_beca_valor: concepto.precio_bs_media_beca_valor || '',
      tasa_bs: concepto.tasa_bs || 'USD',
      mes_correspondiente: concepto.mes_correspondiente || '',
      ano_correspondiente: concepto.ano_correspondiente || new Date().getFullYear(),
      fecha_limite: concepto.fecha_limite ? concepto.fecha_limite.split('T')[0] : '',
    });
    setEditingId(concepto.id);
    setShowForm(true);
    setError('');
  };

  const handleToggle = async (id) => {
    try {
      await conceptosPagoAPI.toggleActivo(id);
      cargarConceptos();
    } catch (err) {
      console.error('Error toggling:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await conceptosPagoAPI.delete(id);
      setDeleteConfirm(null);
      cargarConceptos();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
      setDeleteConfirm(null);
    }
  };

  const openNew = () => {
    setForm(initialForm);
    setEditingId(null);
    setShowForm(true);
    setError('');
  };

  const getTipo = (tipo) => TIPOS.find(t => t.value === tipo) || TIPOS[4];

  // Get the BCV rate for the Bs section based on tasa_bs selection
  const getTasaBs = () => form.tasa_bs === 'EUR' ? tasaEUR : tasaUSD;

  // Group concepts by type
  const grouped = conceptos.reduce((acc, c) => {
    const tipo = c.tipo;
    if (!acc[tipo]) acc[tipo] = [];
    acc[tipo].push(c);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-lsfc-green-600 to-lsfc-green-700 text-white px-4 py-6 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">Conceptos de Pago</h1>
              <p className="text-green-100 text-sm mt-1">
                Configura los pagos que deben realizar los representantes
              </p>
              {(tasaUSD || tasaEUR) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {tasaUSD && (
                    <span className="bg-white/20 px-2 py-0.5 rounded text-xs">
                      💵 BCV/Dolar: {tasaUSD.toFixed(2)} Bs
                    </span>
                  )}
                  {tasaEUR && (
                    <span className="bg-white/20 px-2 py-0.5 rounded text-xs">
                      💶 BCV/Euro: {tasaEUR.toFixed(2)} Bs
                    </span>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={openNew}
              className="bg-white text-lsfc-green-700 px-4 py-2 rounded-lg font-semibold hover:bg-green-50 transition-colors flex items-center gap-2 self-start"
            >
              <span className="text-xl">+</span> Nuevo Concepto
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
          <button
            onClick={() => setFiltroTipo('todos')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filtroTipo === 'todos'
                ? 'bg-lsfc-green-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border'
            }`}
          >
            Todos
          </button>
          {TIPOS.map(t => (
            <button
              key={t.value}
              onClick={() => setFiltroTipo(t.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                filtroTipo === t.value
                  ? 'bg-lsfc-green-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-xl w-full max-w-lg my-8 shadow-2xl">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingId ? 'Editar Concepto' : 'Nuevo Concepto de Pago'}
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {/* Nombre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={e => setForm({ ...form, nombre: e.target.value })}
                    placeholder="Ej: Torneo Apertura Olga Marina 2026"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-lsfc-green-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Tipo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {TIPOS.map(t => (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setForm({ ...form, tipo: t.value })}
                        className={`px-2 py-2 rounded-lg text-xs font-medium border-2 transition-colors ${
                          form.tipo === t.value
                            ? 'border-lsfc-green-500 bg-lsfc-green-50 text-lsfc-green-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {t.icon} {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Descripcion */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion</label>
                  <textarea
                    value={form.descripcion}
                    onChange={e => setForm({ ...form, descripcion: e.target.value })}
                    placeholder="Descripcion opcional..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-lsfc-green-500 focus:border-transparent"
                    rows={2}
                  />
                </div>

                {/* ===== SECTION 1: Precio en Divisas (USD) ===== */}
                <div className="bg-green-50 rounded-lg p-4 space-y-3">
                  <h3 className="text-sm font-bold text-green-800">
                    💵 Precio en Divisas (USD)
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-green-700 mb-1">Regular *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 font-medium text-green-600">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.precio_usd}
                          onChange={e => setForm({ ...form, precio_usd: e.target.value })}
                          className="w-full border border-green-200 rounded-lg pl-7 pr-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="40.00"
                          required
                        />
                      </div>
                      {tasaUSD && form.precio_usd && (
                        <p className="text-xs text-green-500 mt-1">
                          = Bs. {(parseFloat(form.precio_usd) * tasaUSD).toFixed(2)}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-green-700 mb-1">Media Beca</label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 font-medium text-green-600">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.precio_media_beca_usd}
                          onChange={e => setForm({ ...form, precio_media_beca_usd: e.target.value })}
                          className="w-full border border-green-200 rounded-lg pl-7 pr-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="20.00"
                        />
                      </div>
                      {tasaUSD && form.precio_media_beca_usd && (
                        <p className="text-xs text-green-500 mt-1">
                          = Bs. {(parseFloat(form.precio_media_beca_usd) * tasaUSD).toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* ===== SECTION 2: Precio en Bolivares ===== */}
                <div className={`rounded-lg p-4 space-y-3 ${
                  form.tasa_bs === 'EUR' ? 'bg-blue-50' : 'bg-amber-50'
                }`}>
                  <h3 className={`text-sm font-bold ${
                    form.tasa_bs === 'EUR' ? 'text-blue-800' : 'text-amber-800'
                  }`}>
                    🏦 Precio en Bolivares
                  </h3>

                  {/* Rate selector */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                      Tasa BCV para calcular Bs
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, tasa_bs: 'USD' })}
                        className={`p-2 rounded-lg border-2 text-left transition-all text-xs ${
                          form.tasa_bs === 'USD'
                            ? 'border-amber-500 bg-amber-100'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <p className="font-bold text-gray-800">💵 BCV / Dolar</p>
                        {tasaUSD && (
                          <p className="text-gray-500 mt-0.5">{tasaUSD.toFixed(2)} Bs/$</p>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, tasa_bs: 'EUR' })}
                        className={`p-2 rounded-lg border-2 text-left transition-all text-xs ${
                          form.tasa_bs === 'EUR'
                            ? 'border-blue-500 bg-blue-100'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <p className="font-bold text-gray-800">💶 BCV / Euro</p>
                        {tasaEUR && (
                          <p className="text-gray-500 mt-0.5">{tasaEUR.toFixed(2)} Bs/&euro;</p>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Bs prices */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${
                        form.tasa_bs === 'EUR' ? 'text-blue-700' : 'text-amber-700'
                      }`}>
                        Regular
                      </label>
                      <div className="relative">
                        <span className={`absolute left-3 top-2 font-medium ${
                          form.tasa_bs === 'EUR' ? 'text-blue-600' : 'text-amber-600'
                        }`}>
                          {form.tasa_bs === 'EUR' ? '\u20AC' : '$'}
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.precio_bs_valor}
                          onChange={e => setForm({ ...form, precio_bs_valor: e.target.value })}
                          className={`w-full border rounded-lg pl-7 pr-3 py-2 focus:ring-2 focus:border-transparent ${
                            form.tasa_bs === 'EUR'
                              ? 'border-blue-200 focus:ring-blue-500'
                              : 'border-amber-200 focus:ring-amber-500'
                          }`}
                          placeholder={form.tasa_bs === 'EUR' ? '50.00' : '40.00'}
                        />
                      </div>
                      {(() => {
                        const tasa = getTasaBs();
                        if (tasa && form.precio_bs_valor) {
                          return (
                            <p className={`text-xs mt-1 ${form.tasa_bs === 'EUR' ? 'text-blue-500' : 'text-amber-500'}`}>
                              = Bs. {(parseFloat(form.precio_bs_valor) * tasa).toFixed(2)}
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${
                        form.tasa_bs === 'EUR' ? 'text-blue-700' : 'text-amber-700'
                      }`}>
                        Media Beca
                      </label>
                      <div className="relative">
                        <span className={`absolute left-3 top-2 font-medium ${
                          form.tasa_bs === 'EUR' ? 'text-blue-600' : 'text-amber-600'
                        }`}>
                          {form.tasa_bs === 'EUR' ? '\u20AC' : '$'}
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.precio_bs_media_beca_valor}
                          onChange={e => setForm({ ...form, precio_bs_media_beca_valor: e.target.value })}
                          className={`w-full border rounded-lg pl-7 pr-3 py-2 focus:ring-2 focus:border-transparent ${
                            form.tasa_bs === 'EUR'
                              ? 'border-blue-200 focus:ring-blue-500'
                              : 'border-amber-200 focus:ring-amber-500'
                          }`}
                          placeholder={form.tasa_bs === 'EUR' ? '25.00' : '20.00'}
                        />
                      </div>
                      {(() => {
                        const tasa = getTasaBs();
                        if (tasa && form.precio_bs_media_beca_valor) {
                          return (
                            <p className={`text-xs mt-1 ${form.tasa_bs === 'EUR' ? 'text-blue-500' : 'text-amber-500'}`}>
                              = Bs. {(parseFloat(form.precio_bs_media_beca_valor) * tasa).toFixed(2)}
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>

                  {!form.precio_bs_valor && (
                    <p className="text-xs text-gray-400 italic">
                      Si no se indica precio en Bs, solo se podra pagar en divisas
                    </p>
                  )}
                </div>

                {/* Summary */}
                {form.precio_usd && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="text-xs font-bold text-gray-600 mb-1">El representante podra pagar:</p>
                    <div className="space-y-1.5">
                      {/* Divisas option */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">Divisas</span>
                        <p className="text-sm text-gray-800">
                          <strong>${form.precio_usd}</strong> USD
                        </p>
                      </div>
                      {form.precio_media_beca_usd && (
                        <p className="text-xs text-amber-700 ml-16">
                          🎓 Media Beca: <strong>${form.precio_media_beca_usd}</strong> USD
                        </p>
                      )}

                      {/* Bolivares option */}
                      {form.precio_bs_valor && (
                        <>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                              form.tasa_bs === 'EUR' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              Bolivares
                            </span>
                            <p className="text-sm text-gray-800">
                              <strong>{form.tasa_bs === 'EUR' ? '\u20AC' : '$'}{form.precio_bs_valor}</strong>
                              {' '}a tasa BCV/{form.tasa_bs === 'EUR' ? 'Euro' : 'Dolar'}
                              {(() => {
                                const tasa = getTasaBs();
                                if (tasa) return <span className="text-gray-500"> = <strong>Bs. {(parseFloat(form.precio_bs_valor) * tasa).toFixed(2)}</strong></span>;
                                return null;
                              })()}
                            </p>
                          </div>
                          {form.precio_bs_media_beca_valor && (
                            <p className="text-xs text-amber-700 ml-16">
                              🎓 Media Beca: <strong>{form.tasa_bs === 'EUR' ? '\u20AC' : '$'}{form.precio_bs_media_beca_valor}</strong>
                              {(() => {
                                const tasa = getTasaBs();
                                if (tasa) return <span> = Bs. {(parseFloat(form.precio_bs_media_beca_valor) * tasa).toFixed(2)}</span>;
                                return null;
                              })()}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Periodo */}
                {(form.tipo === 'mensualidad' || form.tipo === 'torneo') && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
                      <select
                        value={form.mes_correspondiente}
                        onChange={e => setForm({ ...form, mes_correspondiente: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-lsfc-green-500 focus:border-transparent"
                      >
                        <option value="">Sin mes</option>
                        {MESES.map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ano</label>
                      <input
                        type="number"
                        min="2024"
                        max="2030"
                        value={form.ano_correspondiente}
                        onChange={e => setForm({ ...form, ano_correspondiente: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-lsfc-green-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}

                {/* Fecha limite */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Limite</label>
                  <input
                    type="date"
                    value={form.fecha_limite}
                    onChange={e => setForm({ ...form, fecha_limite: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-lsfc-green-500 focus:border-transparent"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingId(null); }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-lsfc-green-600 text-white rounded-lg hover:bg-lsfc-green-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Guardando...' : (editingId ? 'Actualizar' : 'Crear Concepto')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirm Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
              <h3 className="text-lg font-bold text-gray-800 mb-2">Eliminar concepto?</h3>
              <p className="text-gray-600 text-sm mb-4">
                Se eliminara "<strong>{deleteConfirm.nombre}</strong>" permanentemente.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-lsfc-green-600"></div>
          </div>
        ) : conceptos.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-6xl mb-4">📋</p>
            <p className="text-gray-500 text-lg mb-2">No hay conceptos de pago</p>
            <p className="text-gray-400 text-sm mb-4">Crea el primer concepto para que los representantes puedan registrar pagos</p>
            <button
              onClick={openNew}
              className="bg-lsfc-green-600 text-white px-6 py-2 rounded-lg hover:bg-lsfc-green-700"
            >
              + Crear Concepto
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {filtroTipo === 'todos' ? (
              Object.entries(grouped).map(([tipo, items]) => {
                const tipoInfo = getTipo(tipo);
                return (
                  <div key={tipo}>
                    <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      {tipoInfo.icon} {tipoInfo.label}
                      <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full text-xs">{items.length}</span>
                    </h2>
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {items.map(c => (
                        <ConceptoCard
                          key={c.id}
                          concepto={c}
                          tipoInfo={tipoInfo}
                          tasaUSD={tasaUSD}
                          tasaEUR={tasaEUR}
                          onEdit={handleEdit}
                          onToggle={handleToggle}
                          onDelete={setDeleteConfirm}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {conceptos.map(c => (
                  <ConceptoCard
                    key={c.id}
                    concepto={c}
                    tipoInfo={getTipo(c.tipo)}
                    tasaUSD={tasaUSD}
                    tasaEUR={tasaEUR}
                    onEdit={handleEdit}
                    onToggle={handleToggle}
                    onDelete={setDeleteConfirm}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ConceptoCard({ concepto: c, tipoInfo, tasaUSD, tasaEUR, onEdit, onToggle, onDelete }) {
  const isVencido = c.fecha_limite && new Date(c.fecha_limite) < new Date();
  const precioUSD = parseFloat(c.precio_usd) || 0;
  const precioBecaUSD = c.precio_media_beca_usd ? parseFloat(c.precio_media_beca_usd) : null;

  // Bs pricing
  const hasBs = c.precio_bs_valor && parseFloat(c.precio_bs_valor) > 0;
  const bsValor = hasBs ? parseFloat(c.precio_bs_valor) : null;
  const bsBecaValor = c.precio_bs_media_beca_valor ? parseFloat(c.precio_bs_media_beca_valor) : null;
  const tasaBs = (c.tasa_bs || 'USD') === 'EUR' ? 'EUR' : 'USD';
  const tasaBsRate = tasaBs === 'EUR' ? tasaEUR : tasaUSD;
  const bsSymbol = tasaBs === 'EUR' ? '\u20AC' : '$';

  return (
    <div className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all hover:shadow-md ${
      !c.activo ? 'opacity-60 border-gray-200' : 'border-gray-200'
    }`}>
      {/* Header */}
      <div className={`px-4 py-3 flex items-center justify-between ${
        !c.activo ? 'bg-gray-100' : 'bg-gradient-to-r from-gray-50 to-white'
      }`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-lg">{tipoInfo.icon}</span>
          <h3 className="font-semibold text-gray-800 text-sm truncate">{c.nombre}</h3>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!c.activo && (
            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Inactivo</span>
          )}
          {isVencido && c.activo && (
            <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Vencido</span>
          )}
        </div>
      </div>

      {/* Prices */}
      <div className="px-4 py-3">
        {c.descripcion && (
          <p className="text-xs text-gray-500 mb-3">{c.descripcion}</p>
        )}

        <div className="space-y-2">
          {/* Divisas price */}
          <div>
            <p className="text-xs text-gray-400 font-medium mb-1">💵 Divisas (USD)</p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">👤 Regular</span>
              <span className="font-bold text-green-700">${precioUSD.toFixed(2)}</span>
            </div>
            {precioBecaUSD && (
              <div className="flex items-center justify-between mt-0.5">
                <span className="text-xs text-gray-500">🎓 Media Beca</span>
                <span className="font-bold text-amber-600">${precioBecaUSD.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Bs price */}
          {hasBs && (
            <div className="pt-2 border-t">
              <p className="text-xs text-gray-400 font-medium mb-1">
                🏦 Bolivares
                <span className={`ml-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                  tasaBs === 'EUR' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  tasa {tasaBs === 'EUR' ? 'Euro' : 'Dolar'}
                </span>
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">👤 Regular</span>
                <div className="text-right">
                  <span className={`font-bold ${tasaBs === 'EUR' ? 'text-blue-700' : 'text-amber-700'}`}>
                    {bsSymbol}{bsValor.toFixed(2)}
                  </span>
                  {tasaBsRate && (
                    <span className="text-xs text-gray-400 ml-1">
                      = {(bsValor * tasaBsRate).toFixed(0)} Bs
                    </span>
                  )}
                </div>
              </div>
              {bsBecaValor && (
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-xs text-gray-500">🎓 Media Beca</span>
                  <div className="text-right">
                    <span className="font-bold text-amber-600">
                      {bsSymbol}{bsBecaValor.toFixed(2)}
                    </span>
                    {tasaBsRate && (
                      <span className="text-xs text-gray-400 ml-1">
                        = {(bsBecaValor * tasaBsRate).toFixed(0)} Bs
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Period & Deadline */}
        {(c.mes_correspondiente || c.fecha_limite) && (
          <div className="mt-3 pt-2 border-t flex flex-wrap gap-2">
            {c.mes_correspondiente && (
              <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                📅 {c.mes_correspondiente} {c.ano_correspondiente}
              </span>
            )}
            {c.fecha_limite && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                isVencido ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-600'
              }`}>
                ⏰ {new Date(c.fecha_limite + 'T12:00:00').toLocaleDateString('es-VE')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-4 py-2 bg-gray-50 border-t flex items-center justify-between">
        <button
          onClick={() => onToggle(c.id)}
          className={`text-xs px-3 py-1 rounded-full transition-colors ${
            c.activo
              ? 'text-amber-700 hover:bg-amber-100'
              : 'text-green-700 hover:bg-green-100'
          }`}
        >
          {c.activo ? '⏸ Desactivar' : '▶ Activar'}
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(c)}
            className="text-xs text-blue-600 hover:bg-blue-50 px-3 py-1 rounded-full transition-colors"
          >
            ✏️ Editar
          </button>
          <button
            onClick={() => onDelete(c)}
            className="text-xs text-red-600 hover:bg-red-50 px-3 py-1 rounded-full transition-colors"
          >
            🗑
          </button>
        </div>
      </div>
    </div>
  );
}
